import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { documentService, researchProjectService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";

const ProjectDocumentsManagement = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { isInvestigador, isAdmin } = useAuth();
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [reviewComments, setReviewComments] = useState("");
  const [reviewStatus, setReviewStatus] = useState(1); // 0 = pendiente, 1 = aprobado, 2 = requiere revisión

  useEffect(() => {
    loadProject();
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadProject = async () => {
    try {
      const response = await researchProjectService.getById(projectId);
      setProject(response.data);
    } catch (err) {
      setError("Error al cargar el proyecto");
      console.error(err);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await documentService.getAll({ projectId });
      setDocuments(response.data);
    } catch (err) {
      setError("Error al cargar documentos");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = (document) => {
    setSelectedDocument(document);
    setReviewComments(document.reviewComments || "");
    // Asegurar que reviewStatus sea un número (0, 1, o 2)
    const status =
      typeof document.reviewStatus === "number"
        ? document.reviewStatus
        : document.reviewStatus === "approved"
        ? 1
        : document.reviewStatus === "pending"
        ? 0
        : document.reviewStatus === "needs_revision"
        ? 2
        : 1;
    setReviewStatus(status);
    setOpenReviewDialog(true);
  };

  const handleSubmitReview = async () => {
    try {
      setError("");
      // Asegurar que reviewStatus sea un número
      const statusNumber = parseInt(reviewStatus, 10);
      await documentService.updateReview(selectedDocument.id, {
        reviewStatus: statusNumber,
        reviewComments,
      });
      setSuccess("Revisión guardada exitosamente");
      setOpenReviewDialog(false);
      loadDocuments();
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar la revisión");
    }
  };

  const handleDownload = async (doc) => {
    try {
      setError("");
      const response = await documentService.download(doc.id);

      // Crear blob y descargar (funciona tanto para archivos locales como IPFS)
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data]);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Obtener la extensión del archivo del storagePath o título
      const ext = doc.storagePath ? doc.storagePath.split(".").pop() : "";
      const filename = ext ? `${doc.title}.${ext}` : doc.title;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess("Documento descargado exitosamente");
    } catch (err) {
      console.error("Error downloading document:", err);
      console.error("Error response:", err.response);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.details ||
        err.response?.data?.message ||
        err.message ||
        "Error al descargar el documento";
      setError(errorMessage);
    }
  };

  const getReviewStatusColor = (status) => {
    // Aceptar tanto números como strings para compatibilidad
    const statusNum =
      typeof status === "number"
        ? status
        : status === "approved"
        ? 1
        : status === "pending"
        ? 0
        : status === "needs_revision"
        ? 2
        : parseInt(status, 10);

    switch (statusNum) {
      case 1: // aprobado
        return "success";
      case 2: // requiere revisión
        return "error";
      case 0: // pendiente
      default:
        return "warning";
    }
  };

  const getReviewStatusText = (status) => {
    // Aceptar tanto números como strings para compatibilidad
    const statusNum =
      typeof status === "number"
        ? status
        : status === "approved"
        ? 1
        : status === "pending"
        ? 0
        : status === "needs_revision"
        ? 2
        : parseInt(status, 10);

    switch (statusNum) {
      case 1: // aprobado
        return "Aprobado";
      case 2: // requiere revisión
        return "Requiere Revisión";
      case 0: // pendiente
      default:
        return "Pendiente";
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton
            onClick={() => navigate("/research-projects")}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              Documentos del Proyecto
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {project?.title}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Subido por</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Estado de Revisión</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No hay documentos en este proyecto
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>{doc.title}</TableCell>
                    <TableCell>
                      {doc.User?.name || "Usuario desconocido"}
                    </TableCell>
                    <TableCell>
                      {new Date(doc.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getReviewStatusText(doc.reviewStatus)}
                        color={getReviewStatusColor(doc.reviewStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Descargar">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(doc)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      {(isInvestigador() || isAdmin()) && (
                        <Tooltip title="Revisar">
                          <IconButton
                            size="small"
                            onClick={() => handleReview(doc)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Review Dialog */}
        <Dialog
          open={openReviewDialog}
          onClose={() => setOpenReviewDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Revisar Documento: {selectedDocument?.title}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                select
                label="Estado de Revisión"
                value={reviewStatus}
                onChange={(e) => setReviewStatus(parseInt(e.target.value, 10))}
                SelectProps={{
                  native: true,
                }}
                sx={{ mb: 2 }}
              >
                <option value={0}>Pendiente</option>
                <option value={1}>Aprobado</option>
                <option value={2}>Requiere Revisión</option>
              </TextField>

              <TextField
                fullWidth
                multiline
                rows={6}
                label="Comentarios de Revisión"
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                placeholder="Ingrese sus comentarios sobre el documento..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenReviewDialog(false)}>Cancelar</Button>
            <Button
              onClick={handleSubmitReview}
              variant="contained"
              color="primary"
            >
              Guardar Revisión
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default ProjectDocumentsManagement;
